import Forms, { bubbles as formBubbles, els as formEls, stateClasses } from "../"
import { Modals } from "../../modals"
import FormValidation from "../validation";
import { bubble } from "../../utils/bubble"
import { render } from "../../utils/render"
import { removeChildNodes } from "../../utils/removeChildNodes"
import { dispatchContentLoaded } from "../../generic/eventing"
import { parseHTML } from "../../utils/parseHTML"
import { getAttr } from "../../utils/getAttr"
import { parseJSON } from "../../utils/parseJSON"
import { getLocaleMsg } from "../../utils/getLocaleMsg"
import { makeRequest } from "../../utils/makeRequest"
import { getCheckedBoxes } from "../../utils/getCheckedBoxes";
import { wait } from "../../utils/wait";

export default class FormSend {

    constructor() {
        this.bindEvents();
    }

    bindEvents() {
        document.addEventListener(formBubbles.formValid, (e) => this.handleFormValid(e));
    }

    handleFormValid({ target }) {
        const cfg = Forms.getFormCfg(target);
        const { isIgnoreAutoSend, isUseFetch, isConfirmBeforeSubmit, handler } = cfg;
        if(!isIgnoreAutoSend) {
            const send = () => {
                if(isUseFetch) {
                    FormSend.send(target, FormSend.getFormData(target, false, [], false))
                      .then(({ resp, cfg }) => {
                          return FormSend.handleSuccess(resp, cfg);
                      })
                      .catch(({ err, cfg }) => {
                          return FormSend.handleError(err, cfg);
                      })
                      .finally(() => {
                          FormSend.handleDone(cfg);
                      });
                } else {
                    target.submit()
                }
            };
            if(isConfirmBeforeSubmit) {
                if(handler && handler.matches(formEls.skipConfirmation)) {
                    send();
                } else {
                    Modals.openConfirmModal({}, (instance, e) => {
                        e.preventDefault();
                        send();
                        Modals.closeModal(instance);
                    });
                }
            } else send();
        }
    }

    /**
     * Получение данных формы
     * @param form{HTMLFormElement} - форма
     * @param isGetAll{Boolean=} - получение всех input, включая disabled
     * @param excludedParams{Array=} - исключаемые из данных названия имён
     * @param isTrim{Boolean=} - trim для значений внутри данных
     */
    static getFormData(form, isGetAll = false, excludedParams = [], isTrim = true) {
        const cfg = Forms.getFormCfg(form);
        const fd = new FormData(form);
        if(cfg.isSerializeDisabledInputs || isGetAll) {
            const disabledInputs = form.querySelectorAll("[disabled]");
            disabledInputs.forEach(({ name, value, type }) => {
                const set = (name, value) => fd.set(name, value);
                if(type === "radio") {
                    const radios = getCheckedBoxes(document.querySelectorAll(`[name=${name}]`));
                    if(radios.length) {
                        set(name, radios[0].value);
                    }
                } else {
                    set(name, value);
                }
            });
        }
        [ ...fd.entries() ].forEach(([ name, value ]) => {
            if((isTrim && typeof value === "string" && !value.trim().length) || excludedParams.includes(name)) {
                fd.delete(name);
            }
        })
        return fd;
    }

    /**
     * Добавление новых элементов на страницу
     * @param elements{Array} - массив с объектами новых элементов
     */
    static appendNodes(elements) {
        elements.forEach((item) => {
            const { to, html } = item;
            const position = item.position || undefined;
            const dest = document.querySelector(to);
            if(dest) {
                render(dest, html, position);
                dispatchContentLoaded({
                    content: dest
                });
                bubble(dest, formBubbles.formAppendNodes, html);
            } else {
                console.debug("Can't find nodes with selector: ", dest);
            }
        })
    }

    /**
     * Замена элементов на странице
     * @param elements{Array} - массив с объектами заменяемых элементов
     */
    static replaceNodes(elements) {
        elements.forEach(item => {
            const { selector, html } = item;
            const dest = document.querySelector(selector);
            const newNodes = parseHTML(html);
            if(dest && newNodes.length) {
                dest.replaceWith(newNodes[0]);
                dispatchContentLoaded({ content: dest });
                bubble(dest, formBubbles.formReplaceNodes, html);
            } else {
                console.debug("Can't find nodes with selector: ", dest);
            }
        });
    }

    /**
     * Скрытие элементов со страницы
     * @param selectors{Array} - массив с селекторами элементов
     */
    static hideNodes(selectors) {
        selectors.forEach(selector => {
            const nodes = document.querySelectorAll(selector);
            if(nodes.length) {
                nodes.forEach(node => {
                    node.classList.add(stateClasses.hidden);
                    bubble(node, formBubbles.formRemoveNodes);
                });
            } else {
                console.debug("Can't find nodes with selector: ", selector);
            }
        })
    }

    /**
     * Стандартный обработчик успешной отправки формы
     * @param resp{Object} - ответ сервера
     * @param cfg{Object} - конфигурация формы
     */
    static handleSuccess(resp, cfg) {
        const { success, error } = resp;
        const { form, isShowErrorsAsModals, errorsListPosition, errorsListLayout, errorsListItemLayout, isResetAfterSuccess, isShowModals, successModalId, isCloseAllModalsAfterSuccess, delay, isReturnJSON } = cfg;
        if (isReturnJSON) {
            wait(delay).then(() => {
                const { file, reload, redirect, errors, hideElements, appendElements, replaceElements, innerHTML, modals } = resp;
                const bubbleDetail = { resp, cfg, form };
                if(file) {
                    window.open(resp.file, "downloadFile").focus();
                }
                if(reload) {
                    window.location.reload();
                    return;
                }
                if(redirect) {
                    window.location.href = redirect;
                    return;
                }
                if(errors && Array.isArray(errors)) {
                    bubble(form, formBubbles.formError, bubbleDetail);
                    if (isShowErrorsAsModals) {
                        Modals.openErrorModal(errors, false);
                    } else {
                        render(form, errorsListLayout, errorsListPosition);
                        const errorsBlock = form.querySelector(formEls.errorsList);
                        errors.forEach(errorText => render(errorsBlock, errorsListItemLayout(errorText), "afterbegin"));
                        // FormValidation.resetGoogleCaptcha(form);
                    }
                } else if (!success && !!error) {
                    bubble(form, formBubbles.formError, bubbleDetail);
                    Modals.openErrorModal(error, false);
                } else if (!success) {
                    bubble(form, formBubbles.formError, bubbleDetail);
                    Modals.openErrorModal();
                } else {
                    bubble(form, formBubbles.formSuccess, bubbleDetail);
                    if(innerHTML && typeof innerHTML === "string") {
                        removeChildNodes(form);
                        render(form, innerHTML);
                        dispatchContentLoaded({ content: form });
                    }
                    if(hideElements) {
                        FormSend.hideNodes(hideElements);
                    }
                    if(appendElements) {
                        FormSend.appendNodes(appendElements);
                    }
                    if(replaceElements) {
                        FormSend.replaceNodes(replaceElements);
                    }
                    if(isResetAfterSuccess) {
                        form.reset();
                        FormValidation.resetFiles(form);
                    }
                    if(isShowModals) {
                        if(isCloseAllModalsAfterSuccess) {
                            Modals.closeAllModals();
                        }
                        if(modals) {
                            (Array.isArray(modals)) ? modals.filter(src => src.length).forEach(src => Modals.openHTMLModal({ src })) : Modals.openHTMLModal({ src: modals });
                        } else {
                            Modals.openInlineModal({ src: successModalId });
                        }
                    }
                }
            });
        }
    }

    /**
     * Стандартный обработчик неудачной отправки формы
     * @param err{Object} - ошибка
     * @param cfg{Object} - конфигурация формы
     */
    static handleError(err, cfg) {
        const { form, delay } = cfg;
        const msg = getLocaleMsg("NETWORK_ERROR");
        Modals.openErrorModal(msg, false, err);
        FormSend.setLoadingState(form, false, delay);
    }

    /**
     * Обработчик отправки формы вне зависимости от ответа сервера
     * @param cfg{Object} - конфиг формы
     */
    static handleDone(cfg) {
        const { form, isShowLoader } = cfg;
        if(isShowLoader) {
            FormSend.setLoadingState(form, false);
        }
        // FormValidation.resetGoogleCaptcha(form);
    }

    /**
     * Управление состоянием блокировки формы во время отправки данных
     * @param form{HTMLFormElement} - форма
     * @param isLoading{Boolean=} - состояние блокировки
     * @param timeOut{Number=} - задержка перехода в состояние
     */
    static setLoadingState(form, isLoading = true, timeOut = 0) {
        const cfg = Forms.getFormCfg(form);
        const { successTimeOut, handler, isDisableAllInputsOnSend, isIgnoreAutoSend } = cfg;
        timeOut = timeOut || successTimeOut;
        const fn = () => {
            (isLoading) ? form.classList.add(stateClasses.loading) : form.classList.remove(stateClasses.loading);
            if(isDisableAllInputsOnSend && !isIgnoreAutoSend) {
                form.querySelectorAll("input, textarea, button, select").forEach(item => item.disabled = isLoading)
            } else if(handler && (handler.matches("button") || handler.matches('[type="submit"]'))) {
                handler.disabled = isLoading;
            }
        }
        (typeof timeOut === "number" && timeOut > 0) ? wait(timeOut).then(() => fn()) : fn();
    }

    /**
     * Отправка данных формы на сервер
     * @param form{HTMLFormElement} - форма
     * @param formData{FormData} - данные формы
     * @return Promise
     */
    static async send(
      form,
      formData,
    ) {
        const cfg = Forms.getFormCfg(form);
        const { handler, isClearErrorsBeforeSend, url, method, isReturnJSON, isShowLoader } = cfg;
        const { submitAppend, errorsList } = formEls;
        formData = formData || FormSend.getFormData(form);
        if(isShowLoader) {
            FormSend.setLoadingState(form);
        }
        if(handler && handler.matches(submitAppend)) {
            const extraData = parseJSON(handler.getAttribute(getAttr(submitAppend)));
            extraData.forEach((item) => Object.entries(item).forEach(([ name, value ]) => formData.append(name, value.toString())));
        }
        // FormValidation.extendFiles(form, formData);
        if(isClearErrorsBeforeSend) {
            const errorList = form.querySelector(errorsList);
            if(errorList) {
                errorList.remove();
            }
        }
        if(form.hasAttribute("method")) {
            const nativeMethod = form.getAttribute("method");
            if(nativeMethod !== method) {
                cfg.method = nativeMethod;
            }
        }
        if(!url) {
            cfg.url = (form.hasAttribute("action")) ? form.getAttribute("action") : window.location.origin + window.location.pathname;
        }
        cfg.data = formData;
        if(isReturnJSON) {
            cfg.type = "json";
        }
        if(window.App.isDebug) {
            console.debug("Form sending:", form, cfg, "with data: ", [ ...formData.entries() ]);
        }
        bubble(form, formBubbles.formSending, { cfg })
        return await makeRequest(cfg).then(
          resp => { return Promise.resolve({ resp, cfg }) }, err => { return Promise.reject({ err, cfg })
          });
    }
}
